import React, { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en";
import { useTransactions } from "../../hooks/useTransactions.tsx";
import { useSubscriptions } from "../../hooks/useSubscriptions.tsx";
import { Transaction } from "../../types/transaction.tsx";
import { Subscription } from "../../types/subscription.tsx";
import Modal from "../../components/Modal/index.tsx";
import {
  ArrowRightLeft,
  CalendarSync,
  ChevronLeft,
  ChevronRight,
  Pencil,
  RotateCcw,
} from "lucide-react";
import { AddTransactionForm } from "../../components/AddTransaction/AddTransactionForm.tsx";
import { AddSubscriptionForm } from "../../components/AddSubscription/AddSubscriptionForm.tsx";
import { v4 as uuidv4 } from "uuid";

dayjs.locale("en");

function isSameDay(a: string | Dayjs, b: string | Dayjs) {
  const da = typeof a === "string" ? dayjs(a) : a;
  const db = typeof b === "string" ? dayjs(b) : b;
  return da.isSame(db, "day");
}

// Simple subscription occurrence check: supports none | weekly | monthly | yearly
function isSubscriptionOnDate(sub: Subscription, date: Dayjs): boolean {
  if (sub.isActive === false) return false;
  const start = dayjs(sub.startDate);
  const end = sub.endDate ? dayjs(sub.endDate) : null;
  if (date.isBefore(start, "day")) return false;
  if (end && date.isAfter(end, "day")) return false;
  const rec = (sub.recurrenceType || "none").toLowerCase();
  switch (rec) {
    case "weekly":
      return start.day() === date.day();
    case "monthly":
      return start.date() === date.date();
    case "yearly":
      return start.date() === date.date() && start.month() === date.month();
    case "none":
    default:
      return isSameDay(sub.startDate, date);
  }
}

function formatCurrency(amount: number, currency = "AED") {
  const num = Number.isFinite(amount) ? amount : 0;
  return `${currency} ${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)}`;
}

const CalendarView: React.FC = () => {
  const [month, setMonth] = useState<Dayjs>(dayjs().startOf("month"));
  const [selected, setSelected] = useState<Dayjs>(dayjs().startOf("day"));
  const { transactions, addTransaction, updateTransaction } = useTransactions();
  const { subscriptions, addSubscription, updateSubscription } =
    useSubscriptions();

  const [showTxModal, setShowTxModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [editingTx, setEditingTx] = useState<Transaction | null>(null);
  const [editingSub, setEditingSub] = useState<Subscription | null>(null);
  const [listPage, setListPage] = useState(0);
  const listPageSize = 10;
  const [kindFilter] = useState<"all" | "tx" | "sub">("all");

  const calendarCells = useMemo(() => {
    const start = month.startOf("month").startOf("week");
    const end = month.endOf("month").endOf("week");
    const cells: Dayjs[] = [];
    let cur = start;
    while (cur.isBefore(end) || cur.isSame(end, "day")) {
      cells.push(cur);
      cur = cur.add(1, "day");
    }
    return cells;
  }, [month]);

  const itemsForSelected = useMemo(() => {
    const tx = (transactions || []).filter((t: Transaction) =>
      isSameDay(t.date, selected)
    );
    const subs = (subscriptions || []).filter((s: Subscription) =>
      isSubscriptionOnDate(s, selected)
    );
    return { tx, subs };
  }, [transactions, subscriptions, selected]);

  // Combined right-panel list with filter and pagination
  type CombinedItem =
    | {
        kind: "tx";
        id: string;
        title: string;
        amount: number;
        currency: string;
        raw: Transaction;
      }
    | {
        kind: "sub";
        id: string;
        title: string;
        amount: number;
        currency: string;
        raw: Subscription;
      };

  const combinedItems = useMemo<CombinedItem[]>(() => {
    const txItems: CombinedItem[] = (itemsForSelected.tx || []).map((t) => ({
      kind: "tx",
      id: t.id,
      title: t.title,
      amount: t.amount,
      currency: t.currency,
      raw: t,
    }));
    const subItems: CombinedItem[] = (itemsForSelected.subs || []).map((s) => ({
      kind: "sub",
      id: s.id,
      title: s.title,
      amount: s.amount,
      currency: s.currency,
      raw: s,
    }));
    let all = [...txItems, ...subItems];
    if (kindFilter !== "all") all = all.filter((i) => i.kind === kindFilter);
    return all;
  }, [itemsForSelected, kindFilter]);

  // Reset pagination on date/filter change
  React.useEffect(() => {
    setListPage(0);
  }, [selected, kindFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(combinedItems.length / listPageSize)
  );
  const pageSlice = combinedItems.slice(
    listPage * listPageSize,
    listPage * listPageSize + listPageSize
  );

  // Handlers: Transactions
  const handleCreateTx = (data: any) => {
    const dateISO = selected.startOf("day").toISOString();
    const full: Transaction = {
      id: uuidv4(),
      title: data.title,
      type: data.type || "expense",
      amount: Number(data.amount || 0),
      category: data.category || "miscellaneous",
      currency: data.currency || "AED",
      date: dateISO,
      status: data.status || "completed",
      notes: data.notes || "",
    } as any;
    addTransaction(full);
    setShowTxModal(false);
  };

  const handleEditTx = (data: any) => {
    if (!editingTx) return;
    const updated: Transaction = {
      ...editingTx,
      title: data.title ?? editingTx.title,
      amount: Number(data.amount ?? editingTx.amount),
      // keep existing fields (type/category/currency/date/status)
    } as Transaction;
    updateTransaction(updated);
    setEditingTx(null);
    setShowTxModal(false);
  };

  // Handlers: Subscriptions
  const handleCreateSub = (data: any) => {
    const full: Subscription = {
      id: uuidv4(),
      title: data.title,
      type: data.type || "expense",
      amount: Number(data.amount || 0),
      category: data.category || "subscriptions",
      startDate: data.startDate || selected.format("YYYY-MM-DD"),
      endDate: data.endDate || "",
      currency: data.currency || "AED",
      notes: data.notes || "",
      recurrenceType: data.recurrenceType || "monthly",
      isActive: data.isActive !== false,
    } as Subscription;
    addSubscription(full);
    setShowSubModal(false);
  };

  const handleEditSub = (data: any) => {
    if (!editingSub) return;
    const updated: Subscription = {
      ...editingSub,
      title: data.title ?? editingSub.title,
      amount: Number(data.amount ?? editingSub.amount),
      category: data.category ?? editingSub.category,
      startDate: data.startDate ?? editingSub.startDate,
      endDate: data.endDate ?? editingSub.endDate,
      currency: data.currency ?? editingSub.currency,
      notes: data.notes ?? editingSub.notes,
      recurrenceType: (data.recurrenceType as any) ?? editingSub.recurrenceType,
      isActive:
        typeof data.isActive === "boolean"
          ? data.isActive
          : editingSub.isActive,
    } as Subscription;
    updateSubscription(updated);
    setEditingSub(null);
    setShowSubModal(false);
  };

  let activities = [
    ...transactions.map((n: any) => {
      n.activityType = "transactions";
      return n;
    }),
    ...subscriptions.map((n: any) => {
      n.activityType = "subscriptions";
      return n;
    }),
  ];

  return (
    <section className="p-5 flex justify-center">
      <div className="container-site">
        <div className="text-white flex items-start justify-between mb-3 gap-4">
          <div className="flex-1">
            <h1 className="heading-2">Calendar & Activity</h1>
            <p className="sub-heading-2 mt-1 text-white/80">
              Select a date to view that day’s transactions and subscriptions.
            </p>
          </div>
          {/* Dashboard quick link removed; available via SideNav */}
        </div>

        {/* <div className="text-white flex items-center justify-between gap-3 mb-4 border border-white/10 rounded-lg px-3 py-2 bg-white/5">
          <div className="flex items-center gap-2">
            <button className="btn-ghost" onClick={() => { setMonth(dayjs().startOf("month")); setSelected(dayjs().startOf("day")); }}>Today</button>
            <div className="hidden sm:inline-flex rounded-md overflow-hidden border border-white/15">
              <button
                className={`px-3 py-1 text-sm ${kindFilter === 'all' ? 'bg-white/10 text-white' : 'text-white/80'}`}
                onClick={() => setKindFilter('all')}
              >All</button>
              <button
                className={`px-3 py-1 text-sm ${kindFilter === 'tx' ? 'bg-white/10 text-white' : 'text-white/80'}`}
                onClick={() => setKindFilter('tx')}
              >Transactions</button>
              <button
                className={`px-3 py-1 text-sm ${kindFilter === 'sub' ? 'bg-white/10 text-white' : 'text-white/80'}`}
                onClick={() => setKindFilter('sub')}
              >Subscriptions</button>
            </div>
          </div>

          <div className="inline-flex items-center gap-2"></div>

          <div className="flex items-center gap-2">
            <button className="btn-primary" onClick={() => { setEditingTx(null); setShowTxModal(true); }}>
              + Transaction
            </button>
            <button className="btn-secondary" onClick={() => { setEditingSub(null); setShowSubModal(true); }}>
              + Subscription
            </button>
          </div>
        </div> */}

        {/* Two-column layout: ~70% calendar, ~30% list */}
        <div className="grid grid-cols-1 lg:grid-cols-[70%_30%] gap-4 items-stretch">
          {/* Calendar */}
          <div className="card-glass border border-white/10 h-full">
            <div className="flex items-center justify-between p-3 border-b border-white/10">
              <div className="text-white/90 font-semibold">
                {month.format("MMMM YYYY")}
              </div>
              <div className="inline-flex items-center gap-1">
                <button
                  className="btn-ghost p-2"
                  aria-label="Reset to today"
                  title="Today"
                  onClick={() => {
                    setMonth(dayjs().startOf("month"));
                    setSelected(dayjs().startOf("day"));
                  }}
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                <button
                  className="btn-ghost p-2"
                  aria-label="Previous month"
                  title="Previous month"
                  onClick={() => setMonth((m) => m.subtract(1, "month"))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  className="btn-ghost p-2"
                  aria-label="Next month"
                  title="Next month"
                  onClick={() => setMonth((m) => m.add(1, "month"))}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-3 text-xs text-gray-400 grid grid-cols-7">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="text-center py-1">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 p-3 pt-0">
              {calendarCells.map((day) => {
                const inMonth = day.month() === month.month();
                const isSelected = day.isSame(selected, "day");
                // const countTx = (transactions || []).filter((t) =>
                //   isSameDay(t.date, day)
                // ).length;
                // const countSubs = (subscriptions || []).filter((s) =>
                //   isSubscriptionOnDate(s, day)
                // ).length;
                // const total = countTx + countSubs;

                let allActivities = activities?.filter(
                  (t: any) =>
                    (t.activityType === "transactions" && isSameDay(t.date, day)) ||
                    (t.activityType === "subscriptions" && isSubscriptionOnDate(t, day))
                );

                return (
                  <button
                    key={day.toString()}
                    onClick={() => setSelected(day)}
                    className={[
                      "text-left min-h-[92px] p-2 rounded-md border transition-colors flex flex-col",
                      inMonth
                        ? "border-white/10 bg-white/5 hover:bg-white/10"
                        : "border-transparent bg-transparent text-gray-500",
                      isSelected && "ring-1 ring-indigo-400/50",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    title={day.format("DD MMM YYYY")}
                  >
                    {/* <div className="flex justify-between text-xs text-gray-400"> */}
                    <span className="font-medium text-lg text-gray-300">
                      {day.date()}
                    </span>
                    {/* </div> */}
                    {/* show up to 2 item titles */}
                    <div className="h-4">
                      {allActivities.slice(0, 2).map((t) => (
                        <div
                          key={t.id}
                          className="flex items-center truncate text-[11px] text-gray-300 gap-1"
                        >
                          <div className="text-sm rounded-sm">
                            {t.activityType === "transactions" && (
                              <ArrowRightLeft size="12px" />
                            )}

                            {t.activityType === "subscriptions" && (
                              <CalendarSync size="12px" />
                            )}
                          </div>
                          <div>{t.title}</div>
                        </div>
                      ))}

                      {allActivities.length > 2 && (
                        <div className="text-[10px]">
                          + {allActivities.length - 2} more...
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right list */}
          <div className="card-glass border border-white/10 h-full flex flex-col">
            <div className="p-3 border-b border-white/10 flex items-center justify-between gap-3">
              <div className="text-white font-semibold">
                {selected.format("dddd, DD MMM YYYY")}
              </div>
              <div className="flex items-center gap-2">
                {/* <div className="inline-flex rounded-md overflow-hidden border border-white/15">
                  <button
                    className={`px-3 py-1 text-sm ${kindFilter === 'all' ? 'bg-white/10 text-white' : 'text-white/80'}`}
                    onClick={() => setKindFilter('all')}
                  >All</button>
                  <button
                    className={`px-3 py-1 text-sm ${kindFilter === 'tx' ? 'bg-white/10 text-white' : 'text-white/80'}`}
                    onClick={() => setKindFilter('tx')}
                  >Tx</button>
                  <button
                    className={`px-3 py-1 text-sm ${kindFilter === 'sub' ? 'bg-white/10 text-white' : 'text-white/80'}`}
                    onClick={() => setKindFilter('sub')}
                  >Subs</button>
                </div> */}
                <button
                  className="btn-ghost p-2"
                  aria-label="Previous page"
                  title="Previous page"
                  disabled={listPage <= 0}
                  onClick={() => setListPage((p) => Math.max(0, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="text-xs text-gray-400 min-w-[56px] text-center">
                  {Math.min(listPage + 1, totalPages)} / {totalPages}
                </div>
                <button
                  className="btn-ghost p-2"
                  aria-label="Next page"
                  title="Next page"
                  disabled={listPage + 1 >= totalPages}
                  onClick={() =>
                    setListPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-2 space-y-2 flex-1 overflow-y-auto">
              {combinedItems.length === 0 ? (
                <div className="text-sm text-gray-400">No items</div>
              ) : (
                pageSlice.map((item) => (
                  <div
                    key={`${item.kind}:${item.id}`}
                    className="flex items-center justify-between px-2 py-4 gap-3 border border-dashed border-white/10"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`inline-flex items-center text-[11px] px-1.5 py-0.5 rounded ${
                          item.kind === "tx"
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "bg-indigo-500/20 text-indigo-300"
                        }`}
                      >
                        {item.kind === "tx" ? "Tx" : "Sub"}
                      </span>
                      <div className="truncate text-sm text-gray-200">
                        {item.title}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-gray-300 truncate">
                        {formatCurrency(item.amount, item.currency)}
                      </div>
                      {item.kind === "tx" ? (
                        <button
                          className="btn-ghost p-2"
                          onClick={() => {
                            
                            setEditingTx(item.raw as Transaction);
                            setShowTxModal(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          className="btn-ghost p-2"
                          onClick={() => {
                            setEditingSub(item.raw as Subscription);
                            setShowSubModal(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Modals */}
        <Modal
          isOpen={showTxModal}
          onClose={() => {
            setShowTxModal(false);
            setEditingTx(null);
          }}
          title={editingTx ? "Edit Transaction" : "Add Transaction"}
        >
          <AddTransactionForm
            onSubmit={editingTx ? handleEditTx : handleCreateTx}
            onError={() => {}}
            defaultValues={
              editingTx
                ? {
                    title: editingTx.title,
                    amount: editingTx.amount,
                    type: editingTx.type,
                    category: editingTx.category,
                    currency: editingTx.currency,
                    date: editingTx.date,
                  }
                : {}
            }
          />
        </Modal>

        <Modal
          isOpen={showSubModal}
          onClose={() => {
            setShowSubModal(false);
            setEditingSub(null);
          }}
          title={editingSub ? "Edit Subscription" : "Add Subscription"}
        >
          <AddSubscriptionForm
            onSubmit={editingSub ? handleEditSub : handleCreateSub}
            onError={() => {}}
            defaultValues={
              editingSub
                ? {
                    title: editingSub.title,
                    type: editingSub.type as any,
                    amount: editingSub.amount,
                    category: editingSub.category,
                    startDate: editingSub.startDate,
                    endDate: editingSub.endDate,
                    currency: editingSub.currency,
                    notes: editingSub.notes,
                    recurrenceType: editingSub.recurrenceType as any,
                    isActive: editingSub.isActive,
                  }
                : { startDate: selected.format("YYYY-MM-DD") }
            }
          />
        </Modal>
      </div>
    </section>
  );
};

export default CalendarView;
